import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import api from "@/fileshare/lib/api";
import { useAuth } from "@/fileshare/context/AuthContext";
import { formatApiErrorDetail } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Label } from "@/fileshare/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.user);
      toast.success("Welcome back!");
      navigate("/fileshares/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap className="max-w-md mx-auto px-6 pt-20 pb-24">
      <div className="flex flex-col items-start mb-8">
        <div className="h-10 w-10 rounded-md bg-blue-500 flex items-center justify-center mb-5">
          <Share2 className="h-5 w-5 text-white" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Welcome back</h1>
        <p className="text-sm text-slate-500 mt-1">Log in to manage your files.</p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-sm text-slate-700">Email</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 bg-slate-50/60 border-slate-200" />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Label className="text-sm text-slate-700">Password</Label>
            <Link to="/fileshares/forgot-password" className="text-xs text-blue-500 hover:underline" data-testid="forgot-link">Forgot?</Link>
          </div>
          <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 bg-slate-50/60 border-slate-200" />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-red-500">{error}</p>}
        <motion.div whileTap={{ scale: 0.99 }}>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11">
            {loading ? "Logging in…" : "Log in"}
          </Button>
        </motion.div>
      </form>

      <p className="text-sm text-slate-500 mt-6">
        No account?{" "}
        <Link to="/fileshares/signup" className="text-blue-500 font-medium hover:underline" data-testid="goto-signup">Create one</Link>
      </p>
    </PageWrap>
  );
}
